import locale from "@bonliva-crm/locale";
import { useEffect, useMemo, useState } from "react";
import {
  Divider,
  makeStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Typography
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { useGlobalStore } from "@hooks";
import { professions } from "@bonliva-crm/common";
import { SimpleProfessionFilterData } from "../utils";
import { CheckboxGroup, Color } from "@bonliva-crm/ui";
import { encodeValue, Option } from "../../profession-filter/utils";

type Props = {
  initialValues: SimpleProfessionFilterData;
  open: boolean;
  setOpen: (open: boolean) => void;
  onClear: () => void;
  onSubmit: (values: SimpleProfessionFilterData) => void;
};

export const ProfessionFilterModal = ({
  initialValues,
  open,
  setOpen,
  onClear,
  onSubmit
}: Props) => {
  const [selectedProfessions, setSelectedProfessions] = useState<string[]>(
    initialValues?.professions ?? []
  );
  const [selectedAreaOfExpertises, setSelectedAreaOfExpertises] = useState<string[]>(
    initialValues?.areaOfExpertises ?? []
  );
  const [professionSearchQuery, setProfessionSearchQuery] = useState("");
  const [areaOfExpSearchQuery, setAreaOfExpSearchQuery] = useState("");
  const classes = useStyles({ open });

  useEffect(() => {
    setSelectedProfessions(initialValues?.professions ?? []);
    setSelectedAreaOfExpertises(initialValues?.areaOfExpertises ?? []);
  }, [initialValues]);

  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  const handleSave = () => {
    onSubmit({
      professions: selectedProfessions,
      areaOfExpertises: selectedAreaOfExpertises
    });
    setOpen(false);
  };

  const handleProfessionChange = (selectedProfessions: string[]) => {
    setSelectedProfessions(selectedProfessions);
    setSelectedAreaOfExpertises([]);
  };

  const store = useGlobalStore();
  const optionsProfessions = useMemo(() => {
    return [
      ...store.userService
        .getUserProfessions()
        .sort()
        .toLabelValue((x) => professions.getProfessionDisplayName(x)),
      { label: "Saknar yrke", value: "None" }
    ] as Option[];
  }, [store]);

  const filteredProfessions = useMemo(() => {
    return optionsProfessions.filter((option) =>
      option.label.toLowerCase().includes(professionSearchQuery.toLowerCase())
    );
  }, [optionsProfessions, professionSearchQuery]);

  const optionsAreaOfExp = useMemo(() => {
    const areas = [];

    store.cache.getAreaOfExpOnProfessions(selectedProfessions).forEach((area) => {
      if (selectedProfessions.includes(area.Profession)) {
        areas.push({
          label: area.Name,
          value: encodeValue(area.Profession, area.Name)
        });
      }
    });

    return areas;
  }, [store, selectedProfessions]);

  const filteredAreasOfExp = useMemo(() => {
    return optionsAreaOfExp.filter((option) =>
      option.label.toLowerCase().includes(areaOfExpSearchQuery.toLowerCase())
    );
  }, [optionsAreaOfExp, areaOfExpSearchQuery]);

  return (
    <Dialog open={open} maxWidth="lg" fullWidth onClose={() => setOpen(false)}>
      <DialogTitle>{locale.Profession}</DialogTitle>
      <DialogContent style={{ height: "100vh" }}>
        <span className={classes.root}>
          {/* Left side */}
          <span className={classes.content}>
            <div className={classes.stickyInput}>
              <TextField
                hiddenLabel
                placeholder="Sök"
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: <Search /> }}
                value={professionSearchQuery}
                onChange={(e) => setProfessionSearchQuery(e.target.value)}
              />
            </div>
            <CheckboxGroup
              value={selectedProfessions}
              onChange={(_, selectedProfessions) =>
                handleProfessionChange(selectedProfessions)
              }
              options={filteredProfessions}
            />
          </span>

          {/* Divider */}
          <Divider flexItem orientation="vertical" />

          {/* Right side */}
          <span className={classes.content}>
            {selectedProfessions?.length === 1 ? (
              <>
                <div className={classes.stickyInput}>
                  <TextField
                    hiddenLabel
                    placeholder="Sök"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{ startAdornment: <Search /> }}
                    value={areaOfExpSearchQuery}
                    onChange={(e) => setAreaOfExpSearchQuery(e.target.value)}
                  />
                </div>
                <CheckboxGroup
                  value={selectedAreaOfExpertises}
                  onChange={(_, value) => setSelectedAreaOfExpertises(value)}
                  options={filteredAreasOfExp}
                />
              </>
            ) : (
              <Typography variant="body1" align="center" color="textSecondary">
                {locale.SelectOneProfessionFilter}
              </Typography>
            )}
          </span>
        </span>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{locale.Cancel}</Button>
        <Button onClick={handleClear}>{locale.Clear}</Button>
        <Button color="primary" onClick={handleSave}>
          {locale.OK}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type StyleProps = {
  open: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
    width: "100%",
    boxSizing: "border-box",
    gap: theme.spacing(1),
    height: "100%"
  },
  stickyInput: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: Color.White,
    paddingBottom: theme.spacing(1),
    width: "100%",
    paddingRight: theme.spacing(1)
  },
  content: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
    backgroundColor: Color.White,
    gap: theme.spacing(1),
    overflowY: "auto",
    maxHeight: "100vh",
    "& > :last-child": {
      padding: theme.spacing(1)
    }
  },
  openModalDiv: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "4px",
    height: "25px",
    padding: "18.5px 12.5px ",
    cursor: "pointer"
  },
  chevron: (props: StyleProps) => ({
    width: "1rem",
    height: "1rem",
    transform: props.open ? "rotate(-180deg)" : "rotate(0deg)",
    transition: "0.2s ease",
    marginRight: "0.1rem"
  })
}));
